import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@scio3/ui';
import { analyticsOrderDevicesOpened } from '@/lib/analytics-service';

/** Solid shopping cart (from Cart.svg). Filled with a teal→mint gradient at rest;
 *  the button overrides it to white (via [&_path]:fill-white) on the gradient
 *  square when hovered / active. */
function CartSolidIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 23 22" className={className} aria-hidden>
      <defs>
        <linearGradient id="order-cart-gradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1ab394" />
          <stop offset="100%" stopColor="#98ddcf" />
        </linearGradient>
      </defs>
      <path
        fill="url(#order-cart-gradient)"
        d="M4.9502 19.9502C4.9502 18.8456 5.84563 17.9502 6.9502 17.9502C8.05467 17.9503 8.9502 18.8457 8.9502 19.9502C8.95009 21.0546 8.05461 21.9501 6.9502 21.9502C5.84569 21.9502 4.9503 21.0547 4.9502 19.9502ZM15.9502 19.9502C15.9502 18.8456 16.8456 17.9502 17.9502 17.9502C19.0547 17.9503 19.9502 18.8457 19.9502 19.9502C19.9501 21.0546 19.0546 21.9501 17.9502 21.9502C16.8457 21.9502 15.9503 21.0547 15.9502 19.9502ZM3 0C3.47158 0 3.87878 0.329894 3.97754 0.791016L4.87891 5H21.04C21.3433 5.00001 21.6305 5.13746 21.8203 5.37402C22.0101 5.61057 22.0823 5.92075 22.0166 6.2168L20.3662 13.6465L20.3652 13.6455C20.2183 14.312 19.8499 14.9089 19.3184 15.3369C18.7865 15.7651 18.1242 15.9989 17.4414 16H7.66016C6.97023 16.0103 6.29764 15.7825 5.75586 15.3545C5.20835 14.922 4.82902 14.3121 4.68262 13.6299L2.19141 2H1C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H3Z"
      />
    </svg>
  );
}

/**
 * Order Devices nav button. Rest: gradient-filled cart, no background, no animation.
 * Hover: a teal→mint gradient square fades + scales in behind the cart (which turns
 * white) with a shiny sweep. Active (on the order-device route): the gradient square
 * is present (no fade), and the shine is off.
 */
export function OrderDevicesNavButton() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isActive = pathname.includes('/order-device');

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/app/s/order-device"
            data-testid="nav-order-devices-btn"
            aria-label={t('Order Devices')}
            onClick={() => analyticsOrderDevicesOpened('top-nav')}
            className="group/order relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md"
          >
            {/* Gradient square — fades + scales in on hover for a smooth reveal, and
                stays put (no animation) when active. */}
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 z-0 rounded-md bg-gradient-to-br from-primary to-[#98ddcf] transition-[opacity,transform] duration-300 ease-out',
                isActive
                  ? 'opacity-100'
                  : 'scale-90 opacity-0 group-hover/order:scale-100 group-hover/order:opacity-100',
              )}
            />
            {/* Shiny sweep — visible on hover only; not rendered once active. */}
            {!isActive && (
              <span
                aria-hidden
                className="order-btn-shine pointer-events-none absolute inset-y-0 left-0 z-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-0 group-hover/order:opacity-100"
              />
            )}
            <CartSolidIcon
              className={cn(
                'relative z-10 h-[18px] w-[18px] [&_path]:transition-colors [&_path]:duration-300',
                isActive ? '[&_path]:fill-white' : 'group-hover/order:[&_path]:fill-white',
              )}
            />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t('Order Devices')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
